import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { Popup, Loader } from "semantic-ui-react";
import classNames from "classnames";
import * as _ from "lodash";
import ExcelExport from "../../components/ExcelExport/ExcelExport";
import GlobalTableSettings from "../GlobalTableSettings/GlobalTableSettings";
import ActionMenuButton from "../../components/ActionMenuButton/ActionMenuButton";
import ConfirmationModal from "../../components/ConfirmationModal/ConfirmationModal";
import { setDriveVersionExtraProperties, createEmptyDriveVersion } from "../../utils/pdApiUtils";
import {
  parseNameToUrl,
  parseUrlToName,
  hasWriteAccess,
  searchMatchRule,
  getIndexedValue
} from "../../utils/utils";
import NavigationLink from "../../components/NavigationLink/NavigationLink";
import ModifyDriveParameterBindings from "../../components/ModifyDriveParameterBindings/ModifyDriveParameterBindings";
import { EditActions, editActions } from "../../actions/editActions";
import DriveVersions from "./DriveVersions";
import {
  editedObjectIdSelector,
  isEditActiveSelector,
  editedObjectIdentifierSelector
} from "../../reducers/editReducer";
import { Column, Filter } from "../../components/Table/Migration/LegacyTypes";
import styles from "../../components/ActionMenuButton/ActionMenuButton.module.scss";

import exportIcon from "../../images/export.svg";
import add from "../../images/circle-plus.svg";
import service from "../../images/service-24.png";
import { pdApiActions, PdApiActions } from "../../actions/pdApiActions";

type StateProps = {
  columns: State.ColumnMap;
  mainState: State.Main;
  pdApi: State.PdApi;
  editedObjectId: number | undefined;
  isEditActive: boolean;
  objectIdentifier: string;
};

type DispatchProps = {
  editActions: typeof editActions;
  pdApiActions: typeof pdApiActions;
};

type OwnProps = {
  subMenuRoute: State.DriveBindingsSubmenu;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  match: any;
  isLoadingParameters: boolean;
  selectedColumnsName: keyof State.ColumnMap;
};

// Props of the component
export type DriveBindingsProps = StateProps & DispatchProps & OwnProps;

type DriveBindingsState = {
  activeMenu: State.DriveBindingsSubmenu;
  isModalOpen: boolean;
  tableColumnFilters: {
    [key in State.DriveBindingsSubmenuWithTables]: Filter[];
  };
};

export class DriveBindings extends React.Component<DriveBindingsProps, DriveBindingsState> {
  static subMenus: State.DriveBindingsSubmenu[] = [
    "Add Bindings",
    "Remove Bindings",
    "Drive Versions"
  ];

  state = {
    activeMenu: this.props.match.params.activeMenu
      ? (parseUrlToName(this.props.match.params.activeMenu) as State.DriveBindingsSubmenu)
      : "Add Bindings",
    isModalOpen: false,
    tableColumnFilters: {
      "Drive Versions": []
    }
  };

  componentDidUpdate(): void {
    const parseActiveMenuFromURL = this.props.match.params.activeMenu
      ? (parseUrlToName(this.props.match.params.activeMenu) as State.DriveBindingsSubmenu)
      : "Add Bindings";
    if (this.state.activeMenu !== parseActiveMenuFromURL) {
      this.setState({ activeMenu: parseActiveMenuFromURL });
    }
    if (this.props.pdApi.driveParameters === undefined) {
      this.props.pdApiActions.fetchDriveParameters();
    }
  }

  private setColumnFilters = (
    subMenuRoute: State.DriveBindingsSubmenu,
    newFilters: Filter[]
  ): void => {
    const newFilterSet = { ...this.state.tableColumnFilters, [subMenuRoute]: newFilters };
    this.setState(prevState => ({
      ...prevState,
      tableColumnFilters: newFilterSet
    }));
  };

  render(): JSX.Element {
    const { pdApi, columns, subMenuRoute, mainState, isEditActive, editedObjectId } = this.props;
    const { activeUser } = mainState;
    const { activeMenu, isModalOpen } = this.state;
    const { driveParameters } = pdApi;
    const filename = activeMenu === "Drive Versions" ? "drive_versions" : subMenuRoute;
    let versionIndex = -1;

    // Sorted by year. Could be changed if we want to.
    let rows: Api.DriveVersion[] = [..._.values(pdApi.driveVersions)].sort(function(
      a: Api.DriveVersion,
      b: Api.DriveVersion
    ) {
      return b.year - a.year;
    });
    rows = rows.map(row => setDriveVersionExtraProperties(row as Api.DriveVersion, pdApi));

    if (editedObjectId) {
      versionIndex = rows.findIndex(row => row.id === editedObjectId);
    }

    const filteredRows = this.filterWithUserSelection(
      this.state.tableColumnFilters[activeMenu as State.DriveBindingsSubmenuWithTables],
      rows
    );

    return (
      <div className="table-container">
        <div className="bindingsWrapper">
          <div className="bindingsTabs">
            {DriveBindings.subMenus.map(menu => (
              <NavigationLink
                key={menu}
                to={"/drive/bindings/" + parseNameToUrl(menu)}
                name={menu}
              />
            ))}
          </div>
          {activeMenu === "Drive Versions" && (
            <div className="actionsMenu">
              <div className="bindingActions">
                {filteredRows.length > 0 && hasWriteAccess(activeUser, "Drive") ? (
                  <ActionMenuButton>
                    <div className="actionBtn">
                      <img src={exportIcon} alt="Export to excel" />
                      <ExcelExport
                        rows={filteredRows}
                        columns={columns[activeMenu]}
                        filename={filename}
                      />
                    </div>
                  </ActionMenuButton>
                ) : (
                  <ActionMenuButton>
                    <div className="actionBtn">
                      <img
                        className={`${styles.icon} ${styles.disableImg}`}
                        src={exportIcon}
                        alt="Export to excel"
                      />
                      <span className={styles.disableText} style={{ textTransform: "capitalize" }}>
                        Export
                      </span>
                    </div>
                  </ActionMenuButton>
                )}

                {hasWriteAccess(activeUser, "Drive") && !isEditActive ? (
                  <ActionMenuButton
                    id="Add"
                    onClick={() =>
                      this.props.editActions.startEditing(createEmptyDriveVersion(), true)
                    }
                  >
                    <div className="actionBtn">
                      <img src={add} alt="Add new item" />
                      <span style={{ textTransform: "capitalize" }}>Add {activeMenu}</span>
                    </div>
                  </ActionMenuButton>
                ) : (
                  <ActionMenuButton id="Add">
                    <div className="actionBtn">
                      <img src={add} className={styles.disableImg} alt="Add new item" />
                      <span style={{ textTransform: "capitalize" }} className={styles.disableText}>
                        Add {activeMenu}
                      </span>
                    </div>
                  </ActionMenuButton>
                )}
              </div>
              {
                <Popup
                  trigger={
                    <div className="tableSetting">
                      <ActionMenuButton>
                        <div className="actionBtn">
                          <img src={service} alt="Table settings" />
                          <span>Table Settings</span>
                        </div>
                      </ActionMenuButton>
                    </div>
                  }
                  wide="very"
                  content={
                    <div onClick={(e) => e.stopPropagation()}>
                      <GlobalTableSettings />
                    </div>
                  }
                  on="click"
                  position="bottom right"
                  className="tableSettingspopup"
                />
              }
            </div>
          )}
        </div>
        <div
          className={classNames({
            bindingsTabPanels: activeMenu === "Add Bindings" || activeMenu === "Remove Bindings",
            bindingsTabTable: activeMenu === "Drive Versions"
          })}
        >
          {activeMenu !== "Drive Versions" && this.props.isLoadingParameters ? (
            <Loader inline="centered" content="Loading parameters" active={true} />
          ) : activeMenu === "Add Bindings" ? (
            <ModifyDriveParameterBindings
              mode="Add"
              domains={pdApi.domains}
              parameters={driveParameters ? driveParameters : []}
              driveVersions={pdApi.driveVersions}
              updateParameters={parameters =>
                this.props.editActions.updateParameters("Drive", parameters)
              }
              permission={hasWriteAccess(activeUser, "Drive")}
            />
          ) : activeMenu === "Remove Bindings" ? (
            <ModifyDriveParameterBindings
              mode="Remove"
              domains={pdApi.domains}
              parameters={driveParameters ? driveParameters : []}
              driveVersions={pdApi.driveVersions}
              updateParameters={parameters =>
                this.props.editActions.updateParameters("Drive", parameters)
              }
              permission={hasWriteAccess(activeUser, "Drive")}
            />
          ) : null}
          {activeMenu === "Drive Versions" && (
            <DriveVersions
              {...this.props}
              subMenuRoute="Drive Versions"
              toggleModal={this.toggleModal}
              rows={rows}
              setColumnFilters={this.setColumnFilters}
            />
          )}
        </div>
        {isEditActive && (
          <ConfirmationModal
            isOpen={isModalOpen}
            size="mini"
            header="Confirm delete"
            content={`Are you sure you want to delete ${activeMenu} ${
              versionIndex > 0 ? rows[versionIndex].year : editedObjectId
            }?`}
            confirmText="Confirm"
            cancelText="Cancel"
            onConfirm={() => this.props.editActions.delete("driveVersions")}
            onCancel={this.toggleModal}
          />
        )}
      </div>
    );
  }

  private toggleModal = (): void => {
    this.setState(prevState => ({
      isModalOpen: !prevState.isModalOpen
    }));
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filterWithUserSelection(userFiltering: Filter[] | undefined, rows: any[]): any[] {
    if (!userFiltering || userFiltering.length === 0) {
      return rows;
    }
    const retval = rows.filter(
      row =>
        userFiltering?.every(filter => {
          const column: State.Column | undefined = this.props.columns[
            this.state.activeMenu as State.LiftBindingsSubmenuWithTables
          ].find((col: Column) => col.id === filter.id);
          const propertyPath = column?.ownAccessorName || "";
          const propertyPathArray = propertyPath.split(".").slice(1);
          const rowValue = getIndexedValue(propertyPathArray, row);
          const included =
            filter.value === "all"
              ? true
              : !!rowValue && !!filter.value
              ? column?.hasDropdownFiltering
                ? rowValue.toString() === filter.value.toString()
                : searchMatchRule(rowValue.toString(), filter.value.toString())
              : false;
          return included;
        }, this),
      this
    );
    return retval;
  }
}

const mapStateToProps = (state: State.Root, ownProps: OwnProps) => ({
  columns: state.columns,
  pdApi: state.pdApi,
  mainState: state.main,
  isEditActive: isEditActiveSelector(state.editView),
  editedObjectId: editedObjectIdSelector(state.editView),
  objectIdentifier: editedObjectIdentifierSelector(state.editView, ownProps.subMenuRoute)
});

const mapDispatchToProps = (dispatch: Dispatch<EditActions | PdApiActions>) => ({
  editActions: bindActionCreators({ ...editActions }, dispatch),
  pdApiActions: bindActionCreators({ ...pdApiActions }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(DriveBindings);
